import React, { useState } from 'react';
import {
    Plus, Upload, Download, Search, Filter, Calendar, FileText,
    AlertTriangle, CheckCircle, Calculator, Users, Building, CreditCard,
    Eye, Edit2, Trash2
} from 'lucide-react';
import { Button } from './Button';

export const TDSManagement = () => {
    const [activeTab, setActiveTab] = useState('deductions');
    const [selectedClient, setSelectedClient] = useState('');

    const [tdsDeductions] = useState([
        {
            id: '1',
            deducteeName: 'John Contractor',
            deducteePAN: 'ABCDE1234F',
            natureOfPayment: 'Contractor Payment',
            section: '194C',
            paymentDate: '2024-01-15',
            amountPaid: 50000,
            tdsDeducted: 500,
            status: 'Verified',
            certificateGenerated: true,
            clientId: '1',
            clientName: 'ABC Industries'
        },
        {
            id: '2',
            deducteeName: 'Office Rent Payment',
            deducteePAN: 'FGHIJ5678K',
            natureOfPayment: 'Rent Payment',
            section: '194I',
            paymentDate: '2024-01-10',
            amountPaid: 25000,
            tdsDeducted: 2500,
            status: 'Verified',
            certificateGenerated: false,
            clientId: '1',
            clientName: 'ABC Industries'
        },
        {
            id: '3',
            deducteeName: 'Rajesh Kumar',
            deducteePAN: 'KLMNO9012P',
            natureOfPayment: 'Salary',
            section: '192',
            paymentDate: '2024-01-31',
            amountPaid: 75000,
            tdsDeducted: 7500,
            status: 'Verified',
            certificateGenerated: true,
            clientId: '2',
            clientName: 'XYZ Corp'
        }
    ]);

    const [tdsChallans] = useState([
        {
            id: '1',
            bsrCode: '0123456',
            challanNo: 'CH001',
            depositDate: '2024-01-20',
            amount: 10500,
            status: 'Deposited',
            quarter: 'Q3 2023-24',
            clientId: '1',
            clientName: 'ABC Industries'
        },
        {
            id: '2',
            bsrCode: '0123457',
            challanNo: 'CH002',
            depositDate: '2024-01-25',
            amount: 5000,
            status: 'Pending',
            quarter: 'Q4 2023-24',
            clientId: '2',
            clientName: 'XYZ Corp'
        }
    ]);

    const [tdsReturns] = useState([
        {
            id: '1',
            quarter: 'Q3',
            financialYear: '2023-24',
            filingDate: '2024-01-31',
            dueDate: '2024-01-31',
            status: 'Filed',
            totalDeductions: 150000,
            totalTDS: 10500,
            acknowledgmentNo: 'ACK123456789',
            form: '24Q',
            clientId: '1',
            clientName: 'ABC Industries'
        },
        {
            id: '2',
            quarter: 'Q4',
            financialYear: '2023-24',
            filingDate: '',
            dueDate: '2024-04-30',
            status: 'Pending',
            totalDeductions: 75000,
            totalTDS: 5000,
            form: '24Q',
            clientId: '2',
            clientName: 'XYZ Corp'
        }
    ]);

    const clients = [
        { id: '1', name: 'ABC Industries' },
        { id: '2', name: 'XYZ Corp' },
        { id: '3', name: 'Tech Solutions' }
    ];

    const tabs = [
        {
            id: 'deductions',
            label: 'TDS Deductions',
            icon: Calculator,
            count: tdsDeductions.filter(d => !selectedClient || d.clientId === selectedClient).length
        },
        {
            id: 'challans',
            label: 'Challans',
            icon: CreditCard,
            count: tdsChallans.filter(c => !selectedClient || c.clientId === selectedClient).length
        },
        {
            id: 'returns',
            label: 'TDS Returns',
            icon: FileText,
            count: tdsReturns.filter(r => !selectedClient || r.clientId === selectedClient).length
        },
        { id: 'certificates', label: 'TDS Certificates', icon: CheckCircle, count: 15 },
        { id: 'reconciliation', label: 'Reconciliation', icon: AlertTriangle, count: 3 },
        { id: 'settings', label: 'TDS Settings', icon: Building, count: 0 }
    ];

    const tdsSections = [
        { code: '192', description: 'Salary', rate: 10, threshold: 250000 },
        { code: '194A', description: 'Interest other than on securities', rate: 10, threshold: 5000 },
        { code: '194C', description: 'Contractor/Sub-contractor', rate: 1, threshold: 30000 },
        { code: '194H', description: 'Commission/Brokerage', rate: 5, threshold: 15000 },
        { code: '194I', description: 'Rent', rate: 10, threshold: 240000 },
        { code: '194J', description: 'Professional/Technical Services', rate: 10, threshold: 30000 },
        { code: '194O', description: 'E-commerce', rate: 1, threshold: 500000 }
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Filed':
            case 'Verified':
            case 'Deposited':
                return 'bg-green-100 text-green-800';
            case 'Pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'Error':
            case 'Failed':
            case 'Overdue':
                return 'bg-red-100 text-red-800';
            case 'Draft':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredDeductions = selectedClient
        ? tdsDeductions.filter(d => d.clientId === selectedClient)
        : tdsDeductions;

    const filteredChallans = selectedClient
        ? tdsChallans.filter(c => c.clientId === selectedClient)
        : tdsChallans;

    const filteredReturns = selectedClient
        ? tdsReturns.filter(r => r.clientId === selectedClient)
        : tdsReturns;

    const totalTDSDeducted = filteredDeductions.reduce((sum, deduction) => sum + deduction.tdsDeducted, 0);
    const totalAmountPaid = filteredDeductions.reduce((sum, deduction) => sum + deduction.amountPaid, 0);
    const pendingCertificates = filteredDeductions.filter(d => !d.certificateGenerated).length;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">TDS Management</h1>
                    <div className="flex space-x-3">


                        <Button label="Import TDS Data" />
                        <Button label="Add TDS Entry" />

                    </div>
                </div>

                {/* Client Filter */}
                <div className="flex items-center space-x-4 mb-6">
                    <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <select
                            value={selectedClient}
                            onChange={(e) => setSelectedClient(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">All Clients</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="text-sm text-gray-600">
                        {selectedClient ? `Showing TDS data for ${clients.find(c => c.id === selectedClient)?.name}` : 'Showing all clients'}
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <tab.icon className="h-4 w-4" />
                                <span>{tab.label}</span>
                                {tab.count > 0 && (
                                    <span className="bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* TDS Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total TDS Deducted</p>
                            <p className="text-2xl font-bold text-blue-600">₹{totalTDSDeducted.toLocaleString()}</p>
                            <p className="text-sm text-gray-500">{selectedClient ? 'This client' : 'All clients'}</p>
                        </div>
                        <Calculator className="h-8 w-8 text-blue-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Payments</p>
                            <p className="text-2xl font-bold text-green-600">₹{totalAmountPaid.toLocaleString()}</p>
                            <p className="text-sm text-gray-500">Gross amount</p>
                        </div>
                        <CreditCard className="h-8 w-8 text-green-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Pending Certificates</p>
                            <p className="text-2xl font-bold text-orange-600">{pendingCertificates}</p>
                            <p className="text-sm text-gray-500">To be generated</p>
                        </div>
                        <AlertTriangle className="h-8 w-8 text-orange-500" />
                    </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Returns Filed</p>
                            <p className="text-2xl font-bold text-purple-600">{filteredReturns.filter(r => r.status === 'Filed').length}</p>
                            <p className="text-sm text-gray-500">This FY</p>
                        </div>
                        <FileText className="h-8 w-8 text-purple-500" />
                    </div>
                </div>
            </div>

            {/* TDS Deductions */}
            {activeTab === 'deductions' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">TDS Deductions</h3>
                            <div className="flex space-x-2">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search deductions..."
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                                <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                    <option value="">All Sections</option>
                                    {tdsSections.map(section => (
                                        <option key={section.code} value={section.code}>{section.code} - {section.description}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Deductee Details
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Client
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nature of Payment
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Section
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Payment Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount Paid
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        TDS Deducted
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Certificate
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredDeductions.map((deduction) => (
                                    <tr key={deduction.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">{deduction.deducteeName}</div>
                                                <div className="text-sm text-gray-500">PAN: {deduction.deducteePAN}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {deduction.clientName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {deduction.natureOfPayment}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {deduction.section}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {deduction.paymentDate}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{deduction.amountPaid.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{deduction.tdsDeducted.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${deduction.certificateGenerated ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {deduction.certificateGenerated ? 'Generated' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button className="text-blue-600 hover:text-blue-900">
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button className="text-gray-600 hover:text-gray-900">
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                <button className="text-green-600 hover:text-green-900">
                                                    <Download className="h-4 w-4" />
                                                </button>
                                                <button className="text-red-600 hover:text-red-900">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TDS Challans */}
            {activeTab === 'challans' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-gray-900">TDS Challans</h3>
                            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                                <Plus className="h-4 w-4" />
                                <span>Add Challan</span>
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Client
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        BSR Code
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Challan No
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Quarter
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Deposit Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredChallans.map((challan) => (
                                    <tr key={challan.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {challan.clientName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {challan.bsrCode}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {challan.challanNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {challan.quarter}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {challan.depositDate}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{challan.amount.toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(challan.status)}`}>
                                                {challan.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button className="text-blue-600 hover:text-blue-900">
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button className="text-green-600 hover:text-green-900">
                                                    <Download className="h-4 w-4" />
                                                </button>
                                                <button className="text-gray-600 hover:text-gray-900">
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TDS Returns */}
            {activeTab === 'returns' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-gray-900">TDS Returns Filing</h3>
                                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                                    <Plus className="h-4 w-4" />
                                    <span>Prepare Return</span>
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Client
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Form Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Quarter
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Financial Year
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Due Date
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total TDS
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredReturns.map((returnData) => (
                                        <tr key={returnData.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {returnData.clientName}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {returnData.form}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {returnData.quarter}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {returnData.financialYear}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {returnData.dueDate}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                ₹{returnData.totalTDS.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(returnData.status)}`}>
                                                    {returnData.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex space-x-2">
                                                    <button className="text-blue-600 hover:text-blue-900">
                                                        <Eye className="h-4 w-4" />
                                                    </button>
                                                    <button className="text-green-600 hover:text-green-900">
                                                        <Download className="h-4 w-4" />
                                                    </button>
                                                    <button className="text-purple-600 hover:text-purple-900">File</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* TDS Filing Requirements */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">TDS Filing Requirements Checklist</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <h4 className="font-medium text-gray-900 mb-3">General Details Required</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">TAN Login ID & Password</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Authorized Signatory Details</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">Previous Quarter's TDS Return</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-900 mb-3">Deduction Details</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Party-wise TDS Details</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">Section-wise Breakup</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">Salary Register (for 192)</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-900 mb-3">Challan & Documents</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-700">TDS Challan Copies</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">Rent Agreement (for 194I)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                        <span className="ml-2 text-sm text-gray-700">Form 16 Breakup</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TDS Settings */}
            {activeTab === 'settings' && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">TDS Section Rates & Thresholds</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Section
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        TDS Rate (%)
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Threshold (₹)
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {tdsSections.map((section) => (
                                    <tr key={section.code} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {section.code}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {section.description}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {section.rate}%
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ₹{section.threshold.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};